import * as THREE from 'three';
import { WORLD, TERRAIN, WATER } from '../config';
import { terrainHeight } from './terrain';
import { groundTint } from './biome';
import { TRAIL_GLSL, bindTrailUniforms } from './trail';
import { shoreSurgeGLSL } from './foam';
import { INTERIOR_FRAG_DECL, interiorDarkGLSL, bindInteriorUniforms } from './interior';
import { cloudFieldGLSL, cloudUniforms } from './clouds';

// Streamed terrain. The world is far too big for one mesh, so it is a grid of chunk meshes that
// follows the camera: only chunks near the focus are meshed, and as the player crosses a chunk
// boundary the grid recenters and the chunks that changed cell (or LOD ring) are rebuilt. The
// analytic terrainHeight stays the source of truth (collision, building, grass, nodes all sample
// it); this only governs what is drawn. Vertices are baked in world space so the slope/biome splat
// shader reads world position directly. Meshing is on the main thread, but spread out: it is a
// TOROIDAL follow-grid (a boundary cross only recycles the far edge row, never the near ground) and
// the rebuilds are drained against a per-frame TIME budget (config.chunkBuildMs), so a boundary cross
// fans its remesh out over the next few frames instead of spiking one. The buffers are also reused:
// each chunk owns its typed arrays for life and we re-upload in place (attribute.needsUpdate) rather
// than dispose + reallocate, and every chunk shares ONE index buffer (topology is identical at the
// fixed RES) -- so a remesh allocates nothing on the heap and never churns a GPU buffer.

const CHUNK = WORLD.chunkSize;
const VIEW = WORLD.chunkView;
const RES = WORLD.chunkRes; // segments per chunk side (uniform for now; LOD comes with the worker)
const VPR = RES + 1; // vertices per row
// Coarse biome-colour grid per chunk: cells ~CHUNK/CB wide. The bilinear fill of vertex colours from this
// grid is what widens the biome cross-fade into a soft ground blend (and keeps the cost off the vertices).
const CB = 13;
const CBN = CB + 1;
const CBEL = new Float32Array(CBN * CBN * 3); // reused scratch (one chunk built at a time)
const SPAN = 2 * VIEW + 1;

interface Chunk {
  mesh: THREE.Mesh;
  geom: THREE.BufferGeometry;
  cx: number; cz: number;
  queued: boolean;
  // Per-chunk buffers, allocated once and reused for every remesh (no per-build garbage). build() refills
  // these in place and flags the matching attributes needsUpdate, so the GPU buffer is re-uploaded, never
  // recreated. pos doubles as the height cache the normal pass reads (interior verts) so it isn't resampled.
  pos: THREE.BufferAttribute; uv: THREE.BufferAttribute; biome: THREE.BufferAttribute; nor: THREE.BufferAttribute;
}

// One shared index buffer for every chunk: at the fixed RES the triangle topology is identical, so it is
// built once and assigned to all geometries (uploaded once, bound for all). Saves a 24k-index alloc + GPU
// upload on every single chunk build.
function buildSharedIndex(): THREE.BufferAttribute {
  const idx = new Uint32Array(RES * RES * 6);
  let ii = 0;
  for (let j = 0; j < RES; j++) {
    for (let k = 0; k < RES; k++) {
      const a = j * VPR + k, b = a + 1, c = a + VPR, d = c + 1;
      idx[ii++] = a; idx[ii++] = c; idx[ii++] = b;
      idx[ii++] = b; idx[ii++] = c; idx[ii++] = d;
    }
  }
  return new THREE.BufferAttribute(idx, 1);
}

export class TerrainChunks {
  readonly group = new THREE.Group();
  private readonly material: THREE.Material;
  private readonly chunks: Chunk[] = [];
  private readonly dirty: Chunk[] = []; // mesh queue, drained against a per-frame time budget (chunkBuildMs)
  private readonly uSea = { value: TERRAIN.seaLevel }; // live sea level for the beach-sand band
  private readonly uWaterCol = { value: new THREE.Color(WATER.shallowColor).convertSRGBToLinear() }; // waterline glaze tint
  private curX = NaN;
  private curZ = NaN;

  constructor(renderer: THREE.WebGLRenderer, uTime: { value: number }) {
    this.material = makeTerrainMaterial(renderer, uTime, this.uSea, this.uWaterCol);
    const sharedIndex = buildSharedIndex();
    for (let i = 0; i < SPAN * SPAN; i++) {
      const geom = new THREE.BufferGeometry();
      // Allocate this chunk's vertex buffers once; build() refills them in place forever after.
      const pos = new THREE.BufferAttribute(new Float32Array(VPR * VPR * 3), 3);
      const uv = new THREE.BufferAttribute(new Float32Array(VPR * VPR * 2), 2);
      const biome = new THREE.BufferAttribute(new Float32Array(VPR * VPR * 3), 3);
      const nor = new THREE.BufferAttribute(new Float32Array(VPR * VPR * 3), 3);
      pos.setUsage(THREE.DynamicDrawUsage); uv.setUsage(THREE.DynamicDrawUsage);
      biome.setUsage(THREE.DynamicDrawUsage); nor.setUsage(THREE.DynamicDrawUsage);
      geom.setAttribute('position', pos);
      geom.setAttribute('uv', uv);
      geom.setAttribute('biomeCol', biome);
      geom.setAttribute('normal', nor);
      geom.setIndex(sharedIndex); // shared across all chunks (identical topology at the fixed RES)
      const mesh = new THREE.Mesh(geom, this.material);
      mesh.receiveShadow = true;
      mesh.castShadow = false;
      mesh.visible = false;
      this.group.add(mesh);
      this.chunks.push({ mesh, geom, cx: NaN, cz: NaN, queued: false, pos, uv, biome, nor });
    }
  }

  // Recenter the toroidal grid on the focus (only the new edge cells change slot) and drain the mesh
  // queue. Drain runs every frame, not just on a boundary cross, so a queued chunk always completes.
  update(fx: number, fz: number): void {
    this.uSea.value = TERRAIN.seaLevel; // track the live sea level so the sand band follows the water
    this.uWaterCol.value.set(WATER.shallowColor).convertSRGBToLinear(); // glaze tracks the live water colour (F2)
    const first = !Number.isFinite(this.curX); // first build: mesh everything at once for spawn
    const ccx = Math.floor(fx / CHUNK);
    const ccz = Math.floor(fz / CHUNK);
    if (ccx !== this.curX || ccz !== this.curZ) {
      this.curX = ccx;
      this.curZ = ccz;
      for (let cx = ccx - VIEW; cx <= ccx + VIEW; cx++) {
        for (let cz = ccz - VIEW; cz <= ccz + VIEW; cz++) {
          const sx = ((cx % SPAN) + SPAN) % SPAN;
          const sz = ((cz % SPAN) + SPAN) % SPAN;
          const ch = this.chunks[sx * SPAN + sz];
          if (ch.cx !== cx || ch.cz !== cz) {
            ch.cx = cx;
            ch.cz = cz;
            ch.mesh.visible = false; // hide until remeshed: the slot still holds the previous cell
            if (!ch.queued) { ch.queued = true; this.dirty.push(ch); }
          }
        }
      }
    }
    // Drain against a TIME budget: build at least one chunk, then keep going only while under chunkBuildMs
    // AND the count cap. A single chunk is ~2-3ms, so on a boundary-cross burst this builds ~one heavy chunk
    // per frame and lets the rest fan out over the next frames -- no single-frame spike. The unbuilt chunks
    // are at the fog edge. Boot goes through prime()+pumpBuild() under the loader instead (the old "first
    // update meshes ALL chunks synchronously" path was a multi-second world-start freeze); the `first` bypass
    // stays only as a safety net for any caller that skips the prime.
    let n = 0;
    const t0 = first ? 0 : performance.now();
    while (this.dirty.length > 0) {
      const ch = this.dirty.shift()!;
      ch.queued = false;
      this.build(ch);
      n++;
      if (first) continue;
      if (n >= WORLD.chunkBuildBudget || performance.now() - t0 >= WORLD.chunkBuildMs) break;
    }
  }

  // Boot path: recenter on the spawn and queue the WHOLE grid without building any of it, so the loader
  // can drain the queue in slices over yielded frames (pumpBuild) with a live progress bar. Meshing all
  // SPAN*SPAN chunks in one synchronous hit (~4.7k terrainHeight samples each) froze the whole PC at
  // world start (owner 2026-07-13). After prime(), the first update() sees a warm grid and builds nothing.
  prime(fx: number, fz: number): void {
    this.curX = Math.floor(fx / CHUNK);
    this.curZ = Math.floor(fz / CHUNK);
    for (let cx = this.curX - VIEW; cx <= this.curX + VIEW; cx++) {
      for (let cz = this.curZ - VIEW; cz <= this.curZ + VIEW; cz++) {
        const sx = ((cx % SPAN) + SPAN) % SPAN;
        const sz = ((cz % SPAN) + SPAN) % SPAN;
        const ch = this.chunks[sx * SPAN + sz];
        ch.cx = cx;
        ch.cz = cz;
        ch.mesh.visible = false;
        if (!ch.queued) { ch.queued = true; this.dirty.push(ch); }
      }
    }
  }

  get buildPending(): number { return this.dirty.length; }

  // Build up to maxChunks queued chunks NOW (the boot loader calls this between yielded frames).
  pumpBuild(maxChunks: number): void {
    for (let n = 0; n < maxChunks && this.dirty.length > 0; n++) {
      const ch = this.dirty.shift()!;
      ch.queued = false;
      this.build(ch);
    }
  }

  // Re-bake every live chunk from the current terrainHeight (which reads the live TERRAIN params).
  // Called by the admin panel after a land-gen knob changes. Synchronous: a few dozen chunks remesh
  // in well under a frame at the dev resolution, and the admin debounces calls to slider-release.
  rebuildAll(): void {
    for (const ch of this.chunks) {
      if (Number.isFinite(ch.cx)) this.build(ch);
    }
  }

  private build(ch: Chunk): void {
    const res = RES;
    const vpr = VPR;
    const step = CHUNK / res;
    const ox = ch.cx * CHUNK;
    const oz = ch.cz * CHUNK;
    // Refill this chunk's own buffers in place (allocated once in the ctor) -- no per-build garbage.
    const pos = ch.pos.array as Float32Array;
    const uv = ch.uv.array as Float32Array;
    const biomeCol = ch.biome.array as Float32Array; // per-vertex biome grass colour (linear), interpolated by the splat shader
    // Biome colour on a COARSE grid, bilinear-filled to the vertices below: gives a wide (~CBSTEP-metre)
    // cross-fade between biomes (the requested ground blending) AND is ~20x cheaper than per-vertex.
    const cbStep = CHUNK / CB;
    for (let j = 0; j < CBN; j++) for (let i = 0; i < CBN; i++) {
      const cx = ox + i * cbStep, cz = oz + j * cbStep;
      groundTint(cx, cz, terrainHeight(cx, cz), CBEL, (j * CBN + i) * 3);
    }
    let p = 0, u = 0, c = 0;
    for (let j = 0; j < vpr; j++) {
      for (let k = 0; k < vpr; k++) {
        const x = ox + k * step;
        const z = oz + j * step;
        pos[p++] = x;
        pos[p++] = terrainHeight(x, z);
        pos[p++] = z;
        uv[u++] = x * 0.08; // world-continuous UV for the normal-map detail tiling (tighter = crisper ground)
        uv[u++] = z * 0.08;
        // Bilinear sample the coarse biome colour -> smooth biome cross-fade.
        const cu = (k / res) * CB, cv = (j / res) * CB;
        let ci = cu | 0, cj = cv | 0; if (ci >= CB) ci = CB - 1; if (cj >= CB) cj = CB - 1;
        const ftx = cu - ci, ftz = cv - cj;
        const i00 = (cj * CBN + ci) * 3, i10 = i00 + 3, i01 = ((cj + 1) * CBN + ci) * 3, i11 = i01 + 3;
        for (let e = 0; e < 3; e++) {
          biomeCol[c + e] = (CBEL[i00 + e] * (1 - ftx) + CBEL[i10 + e] * ftx) * (1 - ftz)
                          + (CBEL[i01 + e] * (1 - ftx) + CBEL[i11 + e] * ftx) * ftz;
        }
        c += 3;
      }
    }
    // (Index is the shared buffer set in the ctor -- identical topology at the fixed RES, so it's not rebuilt.)
    // Analytic vertex normals from the height field, so they are CONTINUOUS across chunk boundaries.
    // Per-chunk computeVertexNormals leaves each chunk's edge normals computed without the neighbour's
    // triangles, so adjacent chunks light their shared edge differently -> the hard seam. Here interior
    // verts use the grid heights we already stored, and the chunk's outer edge samples terrainHeight just
    // outside; the neighbour chunk samples the SAME world points for its edge, so the normals match exactly.
    const nor = ch.nor.array as Float32Array;
    for (let j = 0; j < vpr; j++) {
      for (let k = 0; k < vpr; k++) {
        const vx = ox + k * step, vz = oz + j * step;
        const hL = k > 0 ? pos[(j * vpr + (k - 1)) * 3 + 1] : terrainHeight(vx - step, vz);
        const hR = k < res ? pos[(j * vpr + (k + 1)) * 3 + 1] : terrainHeight(vx + step, vz);
        const hD = j > 0 ? pos[((j - 1) * vpr + k) * 3 + 1] : terrainHeight(vx, vz - step);
        const hU = j < res ? pos[((j + 1) * vpr + k) * 3 + 1] : terrainHeight(vx, vz + step);
        const nx = hL - hR, ny = 2 * step, nz = hD - hU;
        const inv = 1 / Math.hypot(nx, ny, nz);
        const o3 = (j * vpr + k) * 3;
        nor[o3] = nx * inv; nor[o3 + 1] = ny * inv; nor[o3 + 2] = nz * inv;
      }
    }
    // Re-upload the refilled buffers IN PLACE (no dispose, no realloc -> no GPU buffer churn, no GC), then
    // refresh the cull sphere for the new heights. The index is shared + unchanged, so it isn't touched.
    ch.pos.needsUpdate = true;
    ch.uv.needsUpdate = true;
    ch.biome.needsUpdate = true;
    ch.nor.needsUpdate = true;
    ch.geom.computeBoundingSphere();
    ch.mesh.visible = true;
  }
}

// Slope/height splat material (grass on flats, dirt on mild slopes, rock on cliffs) plus drifting
// cloud shadows, all in the shader. Moved here from scene so every chunk shares one material.
function makeTerrainMaterial(
  _renderer: THREE.WebGLRenderer,
  uTime: { value: number },
  uSea: { value: number },
  uWaterCol: { value: THREE.Color },
): THREE.MeshStandardMaterial {
  // NO normal texture -- deliberately. Every tiling bump texture repeats by definition (the owner
  // caught it at every scale we tried, bombing included), and the derivative-bump replacement aliased
  // into static. The ground reads through the WORLD-SPACE albedo detail below (value noise on world
  // coords: mathematically cannot repeat) + geometry normals. Flat is clean; clean beats busy.
  const mat = new THREE.MeshStandardMaterial({
    color: WORLD.groundColor,
    roughness: 0.96,
    metalness: 0,
  });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = uTime;
    shader.uniforms.uSunDir = cloudUniforms.uSunDir; // for the sun-projected cloud shadow
    shader.uniforms.uCloudCover = cloudUniforms.uCloudCover; // weather cloud-amount bias
    shader.uniforms.uSeaLevel = uSea;
    shader.uniforms.uWaterShallow = uWaterCol;
    shader.uniforms.uSnowStart = { value: TERRAIN.snowStart };
    shader.uniforms.uSnowEnd = { value: TERRAIN.snowEnd };
    bindInteriorUniforms(shader);
    bindTrailUniforms(shader); // trodden-snow trails read the shared trail map
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 biomeCol;\nvarying vec3 vWPos;\nvarying float vSlopeY;\nvarying vec3 vBiome;')
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\n  vWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;\n  vSlopeY = normalize(mat3(modelMatrix) * objectNormal).y;\n  vBiome = biomeCol;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
varying vec3 vWPos; varying float vSlopeY; varying vec3 vBiome;
uniform float uTime; uniform vec3 uSunDir; uniform float uCloudCover; uniform float uSeaLevel; uniform vec3 uWaterShallow; uniform float uSnowStart; uniform float uSnowEnd; ${INTERIOR_FRAG_DECL}
// PRECISION-SAFE hash: the old fract(p*127.1) form quantizes once world coords push the product past
// ~1e6 (fp32 spacing 0.06 there) -- the hash collapses into a visible dot GRID at zoom-out. Wrap the
// integer lattice small first (period 1024 cells = far beyond eyeshot at every scale used), then a
// sin-free Hoskins hash that stays full-precision on the wrapped domain.
float gHash(vec2 p){ p = mod(p, 1024.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
float gNoise(vec2 p){ vec2 i=floor(p),f=fract(p); f=f*f*(3.0-2.0*f); float a=gHash(i),b=gHash(i+vec2(1.0,0.0)),c=gHash(i+vec2(0.0,1.0)),d=gHash(i+vec2(1.0,1.0)); return mix(mix(a,b,f.x),mix(c,d,f.x),f.y); }
float gFbm(vec2 p){ float s=0.0,a=0.5; for(int i=0;i<4;i++){ s+=a*gNoise(p); p*=2.0; a*=0.5; } return s; }
${TRAIL_GLSL}
${shoreSurgeGLSL}
${cloudFieldGLSL}`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
{
  float macro = gFbm(vWPos.xz * 0.02);
  float fine = gNoise(vWPos.xz * 0.5);
  float micro = gNoise(vWPos.xz * 2.3);           // ~0.4m soil grain: close-range texture on every surface
  float speck = gNoise(vWPos.xz * 6.5 + 41.0);    // pebble-scale specks (bright mineral flecks up close)
  float fleck = gNoise(vWPos.xz * 5.1 + 7.0);     // dark organic-debris flecks (offset field, so they don't overlap specks)
  float region = gFbm(vWPos.xz * 0.0035); // very-large-scale tint: whole hillsides drift warm<->cool
  // Grass colour now comes from the per-vertex BIOME tint (interpolated -> smooth biome borders), with the
  // same fine/macro brightness mottle kept so it is not flat.
  vec3 grass = vBiome * (0.80 + 0.30*fine + 0.12*macro);
  vec3 dirt = vec3(0.30,0.21,0.12) * (0.85 + 0.3*fine);
  vec3 rock = vec3(0.40,0.40,0.43) * (0.80 + 0.4*fine);
  float slope = clamp(vSlopeY, 0.0, 1.0);
  // Ecotone noise: jitter the slope value the biome weights read so the rock/dirt edges wander in patchy
  // fingers instead of clean contour bands. Two scales: broad regional drift + finer fringe.
  float edgeN = gFbm(vWPos.xz * 0.035) * 0.7 + gNoise(vWPos.xz * 0.22) * 0.3;
  float slopeR = clamp(slope + (edgeN - 0.5) * 0.14, 0.0, 1.0); // +/-0.07 wobble on the boundary
  // CLIFF STRATA: horizontal weather-band striations on steep faces (world-Y driven, warped by the ecotone
  // noise so the lines wander), plus pale mineral flecks -- exposed rock reads as bedded stone, not one slab.
  float strata = 0.5 + 0.5 * sin(vWPos.y * 1.9 + (edgeN - 0.5) * 5.0);
  rock *= 0.88 + 0.20 * strata * (1.0 - slope);
  rock += vec3(0.055, 0.05, 0.035) * smoothstep(0.86, 0.98, speck);
  float blotch = gNoise(vWPos.xz * 0.09); // NB: 'patch' is a reserved word in GLSL - do not name a var that
  float rockW = smoothstep(0.80, 0.55, slopeR);
  float dirtW = smoothstep(0.965, 0.84, slopeR) * (1.0 - rockW);
  // Patchy interleave: let dirt blotch up into grass on near-flat ground so the transition is mottled.
  dirtW = clamp(dirtW + smoothstep(0.55, 0.85, blotch) * (1.0 - rockW) * 0.45 * smoothstep(0.86, 0.99, slopeR), 0.0, 1.0);
  vec3 terr = mix(grass, dirt, dirtW);
  terr = mix(terr, rock, rockW);
  // EXPOSED LEDGE LIP: the worn band right where grass gives way to rock (sharp ledges, path edges) shows
  // dry packed earth/stone -- a bright eroded rim that makes ledges read as cut-open ground.
  float lip = smoothstep(0.72, 0.80, slopeR) * (1.0 - smoothstep(0.80, 0.90, slopeR));
  terr = mix(terr, vec3(0.47, 0.42, 0.34) * (0.85 + 0.30 * fine + 0.18 * strata), lip * 0.55);
  // Regional tint AFTER grass/dirt/rock but BEFORE sand, so the beach keeps its own colour. Large smooth
  // bands warm (drier) <-> cool (lusher) plus a mid-scale brightness mottle, so big swathes are not flat.
  terr *= mix(vec3(0.92, 0.90, 0.84), vec3(0.98, 1.02, 0.96), smoothstep(0.35, 0.70, region));
  terr *= 0.93 + 0.14 * gNoise(vWPos.xz * 0.013);
  // CLOSE-RANGE DETAIL (all world-space value noise -- it can NEVER tile or repeat):
  terr *= 0.94 + 0.12 * micro;                                                              // soil grain
  terr = mix(terr, terr * 1.45 + vec3(0.035), smoothstep(0.88, 0.975, speck) * 0.30 * (1.0 - rockW)); // scattered bright grit/pebble specks
  terr = mix(terr, terr * 0.62, smoothstep(0.90, 0.985, fleck) * 0.30 * (1.0 - rockW));               // dark twig/debris flecks (subtle -- the real litter is geometry)
  // Beach sand: a tan band hugging the waterline. It covers the shallow seabed just below the sea and
  // the low beach just above, on flat-ish ground (steep cliffs into the water stay rock), then blends
  // up into grass. Tracks the live sea level (uSeaLevel) so it follows the admin water-level slider.
  float dSea = vWPos.y - uSeaLevel + (edgeN - 0.5) * 1.2; // height above sea, lightly jittered (cleaner waterline)
  // Sand variance done right: SMOOTH large-scale character, not high-frequency static. A big warm<->cool
  // drift over hundreds of metres, soft dune light/dark over ~50m (the dominant variance), and wave-wash
  // bands that run PARALLEL to the waterline (driven by height-above-sea, so they hug the shore like real
  // wave marks), with a warp so they are not ruler-straight. Only a whisper of grain (the old 0.20*fine was
  // the "radio static").
  float sRegion = gFbm(vWPos.xz * 0.004);                          // ~250m: warm <-> cool stretches
  float sDune   = gFbm(vWPos.xz * 0.03);                           // ~33m dune patches (clearly visible in view)
  float sBlotch = smoothstep(0.5, 0.82, gNoise(vWPos.xz * 0.07));  // damp / debris dark patches
  float nearW   = 1.0 - smoothstep(0.0, 8.0, dSea);                // 1 at the water .. 0 up the dry beach
  float waveWarp = (gFbm(vWPos.xz * 0.03) - 0.5) * 2.0;
  float waves   = 0.5 + 0.5 * sin((dSea + waveWarp) * 4.5);        // tight wave-wash lines hugging the shore
  vec3 sand = mix(vec3(0.60, 0.555, 0.45), vec3(0.76, 0.685, 0.50), smoothstep(0.28, 0.72, sRegion)); // warm coastal sand (not washed white)
  sand *= 0.80 + 0.34 * sDune;          // bold dune light/dark so the variance is clearly visible
  sand *= 1.0 - 0.20 * sBlotch;         // darker damp / debris blotches
  sand *= 1.0 - 0.16 * waves * nearW;   // wave-wash lines, only near the waterline
  sand *= 0.98 + 0.04 * fine;           // a whisper of grain (no more radio static)
  // Wet sand, LIVE: the wet line follows the same shore surge the foam sheet rides (shared phase fn),
  // running up the beach with each wash and LINGERING as it dries (pow<1 holds the line high while the
  // surge falls) -- the sand the foam just licked stays dark, then fades back.
  float surgeT = oxShoreSurge(vWPos.xz, uTime);
  float wetLine = 0.4 + pow(surgeT, 0.4) * 1.8;
  float wet = 1.0 - smoothstep(-1.5, wetLine, dSea);
  sand *= mix(1.0, 0.62, wet);                    // wet sand is darker
  sand = mix(sand, sand * vec3(0.88, 0.93, 1.04), wet * 0.7); // and cooler/bluer when wet
  float flatW = smoothstep(0.82, 0.93, slope);                      // sand only on gentle ground
  float sandW = flatW
    * smoothstep(-7.0, -3.0, dSea)        // fade in from the deeper seabed
    * (1.0 - smoothstep(4.0, 8.0, dSea)); // fade out into the grassland above
  terr = mix(terr, sand, clamp(sandW, 0.0, 1.0));
  // Snow cap: high ground turns white, ragged by the ecotone noise, and only settles on flatter faces
  // (steep cliffs stay bare rock). Above the biome tint so any biome's peaks read snowy.
  float snowH = smoothstep(uSnowStart, uSnowEnd, vWPos.y + (edgeN - 0.5) * 8.0);
  float snowK = snowH * smoothstep(0.62, 0.86, slope);
  // CLEAN white snow (was reading dirty): bright, barely-mottled, a whisper of blue in the dips.
  vec3 snow = vec3(0.955, 0.97, 1.0) * (0.965 + 0.045 * fine);
  terr = mix(terr, snow, clamp(snowK, 0.0, 1.0));
  // TRODDEN SNOW TRAILS: where the player has walked (shared trail map), the snow packs down --
  // slightly darker and bluer -- and fades back as the map decays. Snow-gated so grassland is untouched.
  float trodT = oxTrail(vWPos.xz);
  terr = mix(terr, terr * vec3(0.78, 0.84, 0.97), trodT * clamp(snowK, 0.0, 1.0));
  // TRODDEN SAND, super faint: walked-over beach darkens a touch (scuffed / kicked-up grain) and fades
  // back as the same shared map decays. Beach-gated, dry sand only (the surf wipes prints below the
  // waterline), never on snow.
  float sandTrod = clamp(sandW, 0.0, 1.0) * (1.0 - clamp(snowK, 0.0, 1.0)) * smoothstep(-0.1, 0.5, dSea);
  terr = mix(terr, terr * vec3(0.885, 0.875, 0.86), trodT * sandTrod * 0.45);
  // WATERLINE GLAZE: the last ~35cm of ground above the sea takes on the shallow-water colour --
  // fragment-EXACT, because the terrain knows its own height, unlike the water sheet whose coarse
  // seabed samples can't predict its clip line. The seam gets colour-matched from both sides and
  // melts. Banded: full at the waterline, gone by ~0.5m underwater so the seabed still reads sandy.
  float hAbove = vWPos.y - uSeaLevel + (edgeN - 0.5) * 0.14;
  float glz = (1.0 - smoothstep(0.02, 0.34, hAbove)) * smoothstep(-0.55, -0.12, hAbove);
  terr = mix(terr, uWaterShallow, glz * 0.8);
  // Cloud SHADOWS: the SAME field as the overhead cloud layer (oxCloud), so the drifting shadows match the
  // clouds in the sky. Darkens the ground under dense cloud -> "clouds on the ground" that move + change.
  terr *= oxCloudShadow(vWPos.xz, uSunDir, uTime, uCloudCover); // sun-projected, weather-driven cloud shadow
  diffuseColor.rgb = terr;
}`,
      )
      // Interior view: force the FINAL color (after lighting + fog) to black beyond a radius AROUND
      // THE PLAYER, so the home disc survives zoom-out and the surround is truly dark, not just dim.
      .replace(
        '#include <fog_fragment>',
        `#include <fog_fragment>\n${interiorDarkGLSL('vWPos.xz')}`,
      );
  };
  mat.customProgramCacheKey = () => 'ground-splat-biome-cloud10-trail';
  return mat;
}
